import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Settings, Loader2, Edit, Trash2, Globe } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', price: '', category: 'standard', show_on_website: true });

  useEffect(() => { fetchServices(); }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(API + '/services');
      setServices(response.data);
    } catch (error) {
      toast.error('Eroare');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (service) => {
    setEditingService(service);
    setForm({ name: service.name, description: service.description, price: service.price.toString(), category: service.category, show_on_website: service.show_on_website !== false });
    setDialogOpen(true);
  };

  const openNewDialog = () => {
    setEditingService(null);
    setForm({ name: '', description: '', price: '', category: 'standard', show_on_website: true });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price) { toast.error('Completați câmpurile'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const data = { name: form.name, description: form.description, price: parseFloat(form.price), category: form.category, show_on_website: form.show_on_website };
      if (editingService) {
        await axios.put(API + '/admin/services/' + editingService.service_id, data, { headers: { Authorization: 'Bearer ' + token }, withCredentials: true });
        toast.success('Serviciu actualizat');
      } else {
        await axios.post(API + '/admin/services', data, { headers: { Authorization: 'Bearer ' + token }, withCredentials: true });
        toast.success('Serviciu adăugat');
      }
      setDialogOpen(false);
      fetchServices();
    } catch (error) {
      toast.error('Eroare');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (service) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(API + '/admin/services/' + service.service_id, { is_active: !service.is_active }, { headers: { Authorization: 'Bearer ' + token }, withCredentials: true });
      toast.success('Status actualizat');
      fetchServices();
    } catch (error) {
      toast.error('Eroare');
    }
  };

  const toggleWebsite = async (service) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(API + '/admin/services/' + service.service_id, { show_on_website: !(service.show_on_website !== false) }, { headers: { Authorization: 'Bearer ' + token }, withCredentials: true });
      toast.success(service.show_on_website !== false ? 'Ascuns de pe website' : 'Afișat pe website');
      fetchServices();
    } catch (error) {
      toast.error('Eroare');
    }
  };

  const deleteService = async (serviceId, serviceName) => {
    if (!confirm(`Sigur doriți să ștergeți serviciul "${serviceName}"? Acesta va fi dezactivat.`)) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(API + '/admin/services/' + serviceId, {
        headers: { Authorization: 'Bearer ' + token },
        withCredentials: true
      });
      toast.success('Serviciu șters');
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la ștergere');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div><h1 className="text-2xl font-semibold text-slate-900">Servicii</h1><p className="text-slate-500">Gestionează lista de servicii și prețuri</p></div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild><Button onClick={openNewDialog} className="bg-blue-600 hover:bg-blue-700 text-white"><Plus className="h-4 w-4 mr-2" />Serviciu nou</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editingService ? 'Editează serviciu' : 'Adaugă serviciu'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><Label>Nume serviciu *</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></div>
              <div><Label>Descriere</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Preț (EUR) *</Label><Input type="number" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} /></div>
                <div><Label>Categorie</Label><Input value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} placeholder="standard" /></div>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <Label>Afișează pe website</Label>
                  <p className="text-xs text-slate-500">Dacă e dezactivat, serviciul apare doar în comenzi și facturi</p>
                </div>
                <Switch checked={form.show_on_website} onCheckedChange={(v) => setForm({...form, show_on_website: v})} data-testid="show-on-website-toggle" />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Anulează</Button>
                <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">{saving ? 'Se salvează...' : 'Salvează'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div> : (
        <div className="grid gap-4">
          {services.map((service) => (
            <Card key={service.service_id} className={`bg-white hover:shadow-md ${!service.is_active ? 'opacity-60' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-slate-900">{service.name}</p>
                      <Badge variant="secondary" className="text-xs">{service.category}</Badge>
                      {!service.is_active && <Badge variant="destructive" className="text-xs">Inactiv</Badge>}
                      {service.show_on_website === false && <Badge variant="outline" className="text-xs text-slate-400 border-slate-300">Ascuns website</Badge>}
                    </div>
                    <p className="text-sm text-slate-500">{service.description}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="text-xl font-bold text-blue-600">{service.price} EUR</p>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => toggleWebsite(service)} title={service.show_on_website !== false ? 'Ascunde de pe website' : 'Afișează pe website'} className={service.show_on_website !== false ? 'text-green-600 hover:text-green-700' : 'text-slate-400 hover:text-slate-600'} data-testid={`toggle-website-${service.service_id}`}>
                        <Globe className="h-4 w-4" />
                      </Button>
                      <Switch checked={service.is_active} onCheckedChange={() => toggleActive(service)} />
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(service)}><Edit className="h-4 w-4" /></Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => deleteService(service.service_id, service.name)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
